/**
 * FinancingTermsField — the loan terms of a Wisconsin offer's financing
 * contingency (legal copy: paperwork-copy-and-rules.v1.md §10,
 * `WI.offer.financing_terms.*`; QA N13 / D7).
 *
 * Shown on a Wisconsin offer (residential, condo or vacant land) while the
 * financing contingency is selected (`financingTermsAsked`). Every term is
 * required and nothing is prefilled:
 *   - the loan type (`financing_loan_type`), residential and condo only, with
 *     a short description (`financing_loan_type_other`) for "Other";
 *   - the term in whole years (`financing_term_years`, 1–40);
 *   - the maximum initial interest rate (`financing_max_rate_percent`, above 0
 *     and at most 20, to 3 decimals);
 *   - the days after acceptance to apply (`financing_apply_days`, 1–365, no
 *     later than the financing deadline).
 * The loan amount is read-only: the offer prints the offer price minus the
 * down payment (`amount`, from `financingLoanAmount`).
 *
 * The inputs are `aria-required`, not `required`: the workspace counter is a
 * real <form>, and native validation would block a price-only counter on an
 * older offer without these terms, whose untouched terms carry forward. The
 * forms check these fields themselves (`financingTermsError`).
 *
 * Rendered by the offer wizard's Terms step and both counter forms. The values
 * live in the form's `contingencies` object; see `serializeContingencies`.
 *
 * Props:
 *   - category: the Wisconsin offer category (`offerTermsCategory`), or null
 *   - contingencies: form state
 *   - onChange(nextContingencies)
 *   - amount: the loan amount (number) or null
 *   - downPaymentPercent: the offer's down payment (100% leaves no loan)
 *   - viewerRole: 'buyer' (default) or 'seller', for the help text
 *   - disabled
 *   - serverErrors: messages from a 422, keyed by field
 */
import React, { useId } from 'react';
import {
  CONDO_CATEGORY,
  FINANCING_APPLY_DAYS_KEY,
  FINANCING_LOAN_TYPES,
  FINANCING_LOAN_TYPE_KEY,
  FINANCING_LOAN_TYPE_OTHER_KEY,
  FINANCING_MAX_RATE_KEY,
  FINANCING_TERMS_COPY as COPY,
  FINANCING_TERM_YEARS_KEY,
  GOVERNMENT_LOAN_TYPES,
  LOAN_TYPE_OTHER,
  MAX_DEADLINE_DAYS,
  MAX_FINANCING_RATE_PERCENT,
  MAX_LOAN_TERM_YEARS,
  MAX_LOAN_TYPE_OTHER_LENGTH,
  MIN_DEADLINE_DAYS,
  MIN_LOAN_TERM_YEARS,
  applyLoanType,
  financingTermsAsked,
  financingTermsFieldErrors,
  formatLoanAmount,
  getLoanType,
  loanTypeAsked,
  storedDownPayment,
} from './contingencyCatalog';
import styles from './FinancingTermsField.module.css';

const textOf = (value) => (value === undefined || value === null ? '' : String(value));

function NumberInput({ id, label, help, value, onChange, error, serverError, disabled, testId, ...input }) {
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>
        {label}
        <span className={styles.required} aria-hidden="true"> *</span>
      </label>
      <input
        id={id}
        className={styles.input}
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-required="true"
        aria-describedby={[help && helpId, error && errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={Boolean(error || serverError) || undefined}
        data-testid={testId}
        {...input}
      />
      {help && <p id={helpId} className={styles.hint}>{help}</p>}
      {error && <p id={errorId} className={styles.error} role="alert">{error}</p>}
      {serverError && serverError !== error && (
        <p className={styles.error} role="alert" data-testid={`contingency-error-${testId}`}>{serverError}</p>
      )}
    </div>
  );
}

export default function FinancingTermsField({
  category,
  contingencies,
  onChange,
  amount = null,
  downPaymentPercent = null,
  viewerRole = 'buyer',
  disabled = false,
  serverErrors = null,
}) {
  const id = useId();
  const helpId = `${id}-help`;
  const typeLabelId = `${id}-type-label`;
  const otherId = `${id}-other`;

  if (!financingTermsAsked(category, contingencies)) return null;

  const seller = viewerRole === 'seller';
  const errors = financingTermsFieldErrors(contingencies, category);
  // A field shows its own problem once it has a value; blanks are listed together below.
  const typed = (key) => textOf(contingencies?.[key]).trim() !== '';
  const errorFor = (key) => (typed(key) ? errors[key] || null : null);
  const server = (key) => serverErrors?.[key] || null;
  const set = (key, value) => onChange({ ...(contingencies || {}), [key]: value });
  const loanType = getLoanType(contingencies);
  const anyBlank = [FINANCING_LOAN_TYPE_KEY, FINANCING_TERM_YEARS_KEY, FINANCING_MAX_RATE_KEY, FINANCING_APPLY_DAYS_KEY]
    .some((key) => errors[key] && !typed(key));
  const storedDown = storedDownPayment(downPaymentPercent);
  const fullDownPayment = storedDown !== null && storedDown >= 100;

  return (
    <fieldset className={styles.terms} data-testid="financing-terms" aria-describedby={helpId}>
      <legend className={styles.title}>
        {COPY.title}
        <span className={styles.required} aria-hidden="true"> *</span>
      </legend>
      <p id={helpId} className={styles.help}>{seller ? COPY.helpSeller : COPY.help}</p>

      {loanTypeAsked(category) && (
        <div className={styles.field}>
          <span id={typeLabelId} className={styles.label}>
            {COPY.loanTypeLabel}
            <span className={styles.required} aria-hidden="true"> *</span>
          </span>
          <div role="radiogroup" aria-labelledby={typeLabelId} aria-required="true" className={styles.types}>
            {FINANCING_LOAN_TYPES.map((type) => (
              <label key={type} className={`${styles.type} ${loanType === type ? styles.typeOn : ''}`}>
                <input
                  type="radio"
                  name={`${id}-loan-type`}
                  value={type}
                  checked={loanType === type}
                  onChange={() => onChange(applyLoanType(contingencies, type))}
                  disabled={disabled}
                  data-testid={`financing-loan-type-${type}`}
                />
                <span>{COPY.loanTypeOption[type]}</span>
              </label>
            ))}
          </div>
          {loanType === LOAN_TYPE_OTHER && (
            <div className={styles.other}>
              <label htmlFor={otherId} className={styles.label}>
                {COPY.otherLabel}
                <span className={styles.required} aria-hidden="true"> *</span>
              </label>
              <input
                id={otherId}
                className={styles.textInput}
                type="text"
                maxLength={MAX_LOAN_TYPE_OTHER_LENGTH}
                value={textOf(contingencies?.[FINANCING_LOAN_TYPE_OTHER_KEY])}
                onChange={(e) => set(FINANCING_LOAN_TYPE_OTHER_KEY, e.target.value)}
                disabled={disabled}
                aria-required="true"
                aria-describedby={`${otherId}-help`}
                aria-invalid={Boolean(server(FINANCING_LOAN_TYPE_OTHER_KEY)) || undefined}
                data-testid="financing-loan-type-other"
              />
              <p id={`${otherId}-help`} className={styles.hint}>
                {errors[FINANCING_LOAN_TYPE_OTHER_KEY] || (seller ? null : COPY.otherHelp)}
              </p>
            </div>
          )}
          {GOVERNMENT_LOAN_TYPES.includes(loanType) && (
            <>
              <p className={styles.note} role="note" data-testid="financing-government-note">
                {COPY.governmentNote}
              </p>
              {category === CONDO_CATEGORY && (
                <p className={styles.note} role="note" data-testid="financing-condo-note">{COPY.condoNote}</p>
              )}
            </>
          )}
          {(server(FINANCING_LOAN_TYPE_KEY) || server(FINANCING_LOAN_TYPE_OTHER_KEY)) && (
            <p className={styles.error} role="alert" data-testid="contingency-error-financing_loan_type">
              {server(FINANCING_LOAN_TYPE_KEY) || server(FINANCING_LOAN_TYPE_OTHER_KEY)}
            </p>
          )}
        </div>
      )}

      <div className={styles.field}>
        <span className={styles.label}>{COPY.amountLabel}</span>
        {fullDownPayment ? (
          <p className={styles.error} role="alert" data-testid="financing-full-down-payment">
            {COPY.fullDownPayment}
          </p>
        ) : (
          typeof amount === 'number' && (
            <p className={styles.amount} data-testid="financing-amount">
              {(seller ? COPY.amountSeller : COPY.amount)(formatLoanAmount(amount))}
            </p>
          )
        )}
      </div>

      <div className={styles.grid}>
        <NumberInput
          id={`${id}-term`}
          label={COPY.termLabel}
          help={COPY.termHelp}
          value={textOf(contingencies?.[FINANCING_TERM_YEARS_KEY])}
          onChange={(text) => set(FINANCING_TERM_YEARS_KEY, text)}
          error={errorFor(FINANCING_TERM_YEARS_KEY)}
          serverError={server(FINANCING_TERM_YEARS_KEY)}
          disabled={disabled}
          testId={FINANCING_TERM_YEARS_KEY}
          inputMode="numeric"
          min={MIN_LOAN_TERM_YEARS}
          max={MAX_LOAN_TERM_YEARS}
          step="1"
        />
        <NumberInput
          id={`${id}-rate`}
          label={COPY.rateLabel}
          help={seller ? null : COPY.rateHelp}
          value={textOf(contingencies?.[FINANCING_MAX_RATE_KEY])}
          onChange={(text) => set(FINANCING_MAX_RATE_KEY, text)}
          error={errorFor(FINANCING_MAX_RATE_KEY)}
          serverError={server(FINANCING_MAX_RATE_KEY)}
          disabled={disabled}
          testId={FINANCING_MAX_RATE_KEY}
          inputMode="decimal"
          min="0"
          max={MAX_FINANCING_RATE_PERCENT}
          step="0.001"
        />
        <NumberInput
          id={`${id}-apply`}
          label={COPY.applyLabel}
          help={COPY.applyHelp}
          value={textOf(contingencies?.[FINANCING_APPLY_DAYS_KEY])}
          onChange={(text) => set(FINANCING_APPLY_DAYS_KEY, text)}
          error={errorFor(FINANCING_APPLY_DAYS_KEY)}
          serverError={server(FINANCING_APPLY_DAYS_KEY)}
          disabled={disabled}
          testId={FINANCING_APPLY_DAYS_KEY}
          inputMode="numeric"
          min={MIN_DEADLINE_DAYS}
          max={MAX_DEADLINE_DAYS}
          step="1"
        />
      </div>

      {anyBlank && (
        <p className={styles.missing} data-testid="financing-terms-missing">{COPY.missing}</p>
      )}
    </fieldset>
  );
}
